/**
 * Story Fit — company-aware variants (spec §8.7). This file is the source
 * of truth for the five rubric layouts' content. The behavioral page's
 * simpler ✓-pill variant is superseded by the General variant here
 * (spec §13) — A7 wires the
 * behavioral submission's `targetCompany` to pick a variant from this map.
 */

export type StoryFitVariant =
  | 'general'
  | 'amazon'
  | 'meta'
  | 'google'
  | 'netflix'

/** `●●●` strong · `●●○` partial · `●○○` weak · `○○○` not signaled. */
export type StoryFitSignal = 'strong' | 'partial' | 'weak' | 'none'

export const STORY_FIT_LEGEND =
  '● ● ● strong · ● ● ○ partial · S/T/A/R tag = where the signal appears'

export interface StoryFitCompetencyRow {
  label: string
  /** S/T/A/R tag — omitted for rows the story doesn't touch at all. */
  tag?: 'S' | 'T' | 'A' | 'R'
  strength: StoryFitSignal
  evidence?: string
  /** Dashed/dim "not signaled" row styling. */
  notSignaled?: boolean
}

export interface StoryFitAmazonData {
  signaledCount: number
  totalCount: number
  signaled: StoryFitCompetencyRow[]
  notSignaled: string[]
  coachTip: string
}

export interface StoryFitGoogleAttribute {
  label: string
  pct: number
  level: 'Strong' | 'Partial'
  evidence: string
}

export interface StoryFitNetflixCard {
  label: string
  strength: StoryFitSignal
  evidence: string
}

export interface StoryFitVariantData {
  variant: StoryFitVariant
  headerChip: string
  hint: string
  /** General only. */
  competencies?: StoryFitCompetencyRow[]
  /** Amazon only. */
  amazon?: StoryFitAmazonData
  /** Meta only. */
  metaRows?: StoryFitCompetencyRow[]
  /** Google only. */
  googleAttributes?: StoryFitGoogleAttribute[]
  /** Netflix only. */
  netflixCards?: StoryFitNetflixCard[]
}

export const MOCK_STORY_FIT_VARIANTS: Record<
  StoryFitVariant,
  StoryFitVariantData
> = {
  general: {
    variant: 'general',
    headerChip: 'No company selected',
    hint: 'General competency read — pick a target company on the question for a rubric-specific fit.',
    competencies: [
      { label: 'Conflict resolution', tag: 'A', strength: 'strong' },
      { label: 'Influence without authority', tag: 'A', strength: 'strong' },
      { label: 'Ownership', tag: 'T', strength: 'partial' },
      { label: 'Learning / growth', strength: 'none', notSignaled: true },
    ],
  },
  amazon: {
    variant: 'amazon',
    headerChip: 'Amazon · LP interview',
    hint: 'Bar raisers score against named LPs — the story must let the interviewer write an LP down.',
    amazon: {
      signaledCount: 5,
      totalCount: 16,
      signaled: [
        {
          label: 'Have Backbone; Disagree & Commit',
          tag: 'A',
          strength: 'strong',
          evidence:
            'Pushed back two levels up, then committed via joint escalation — the literal principle.',
        },
        {
          label: 'Earn Trust',
          tag: 'A',
          strength: 'strong',
          evidence:
            'Escalated with the principal, presenting both options fairly.',
        },
        {
          label: 'Dive Deep',
          tag: 'A',
          strength: 'partial',
          evidence:
            'Two-day spike, 40%-of-endpoints figure — say the number louder.',
        },
        {
          label: 'Bias for Action',
          tag: 'A',
          strength: 'partial',
          evidence: 'Timeboxed the spike instead of debating in the abstract.',
        },
        {
          label: 'Deliver Results',
          tag: 'R',
          strength: 'weak',
          evidence:
            'Claimed but unquantified — the Result gap blocks this LP from scoring.',
        },
      ],
      notSignaled: [
        'Customer Obsession',
        'Ownership',
        'Invent & Simplify',
        'Are Right, A Lot',
        'Learn & Be Curious',
        'Hire & Develop',
        'Highest Standards',
        'Think Big',
        'Frugality',
        "Earth's Best Employer",
        'Success & Scale',
      ],
      coachTip:
        'Coach tip surfaced by AI: 5 LPs from one story is strong — bar raisers only need 2–3 clean ones. Fixing the Result quantification converts Deliver Results from a liability into a sixth.',
    },
  },
  meta: {
    variant: 'meta',
    headerChip: 'Meta · behavioral round',
    hint: 'Six values, all well known — interviewers listen for them by name.',
    metaRows: [
      {
        label: 'Be Direct and Respect Your Colleagues',
        tag: 'A',
        strength: 'strong',
        evidence:
          "Disagreed openly, escalated respectfully — the story's spine.",
      },
      {
        label: 'Move Fast',
        tag: 'A',
        strength: 'partial',
        evidence: 'Two-day spike; facade shipped without slipping launch.',
      },
      {
        label: 'Focus on Long-Term Impact',
        tag: 'R',
        strength: 'partial',
        evidence:
          'Facade now, full migration next quarter — sequenced, not refused.',
      },
      {
        label: 'Meta, Metamates, Me',
        tag: 'A',
        strength: 'partial',
        evidence:
          'Company outcome over being right — joint escalation shows it.',
      },
      {
        label: 'Be Bold',
        strength: 'none',
        notSignaled: true,
        evidence:
          'Story reads risk-averse — reframe the facade as the bold call under deadline.',
      },
      {
        label: 'Live in the Future',
        strength: 'none',
        notSignaled: true,
        evidence:
          "Not this story's job — cover with a different story in the loop.",
      },
    ],
  },
  google: {
    variant: 'google',
    headerChip: 'Google · Googleyness & leadership',
    hint: 'Four attributes, each scored — bars show how much of the rubric this story covers.',
    googleAttributes: [
      {
        label: 'Emergent leadership',
        pct: 88,
        level: 'Strong',
        evidence:
          'Stepped up without the title: spike, data, joint escalation.',
      },
      {
        label: 'General cognitive ability',
        pct: 78,
        level: 'Strong',
        evidence:
          'Structured the tradeoff and found the smallest reversible step.',
      },
      {
        label: 'Googleyness',
        pct: 55,
        level: 'Partial',
        evidence:
          'Comfort with ambiguity shows; intellectual humility would land harder with a reflection beat.',
      },
      {
        label: 'Role-related knowledge',
        pct: 48,
        level: 'Partial',
        evidence:
          'API architecture depth is implied — name the BFF pattern and endpoint analysis explicitly.',
      },
    ],
  },
  netflix: {
    variant: 'netflix',
    headerChip: 'Netflix · dream team culture',
    hint: "Netflix interviews probe the memo's ideas directly — fit is framed in the memo's own language.",
    netflixCards: [
      {
        label: 'Disagree openly, then commit',
        strength: 'strong',
        evidence:
          "The memo's dissent ideal, enacted: open disagreement with a principal, both options presented, committed to the outcome.",
      },
      {
        label: 'Informed captain',
        strength: 'strong',
        evidence:
          "Owned the call as frontend lead after gathering the data — didn't outsource the decision to consensus.",
      },
      {
        label: 'Judgment over process',
        strength: 'partial',
        evidence:
          'Smallest reversible step under deadline — good judgment, but the "why not just say no" reasoning stays implicit.',
      },
      {
        label: 'Selfless candor',
        strength: 'none',
        evidence:
          'No feedback-seeking or self-critique beat — the suggested Reflection addition covers exactly this.',
      },
    ],
  },
}
